import { getContactsAsync, getPermissionsAsync } from 'expo-contacts';
import { AllLocalRecipients } from 'features/v2a/sender/helpers/Sender.helpers';
import { useCallback, useState } from 'react';
import { getUuid } from 'shared/utils/getUuid/getUuid';

export const useLocalContacts = () => {
  const [contacts, setContacts] = useState<AllLocalRecipients[]>([]);
  const [hasPermission, setHasPermission] = useState<boolean | null>(false);
  const [canAskAgain, setCanAskAgain] = useState<boolean>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function getUnformattedPhoneNumber(phone: string) {
    const updatedPhoneNumber = phone
      ?.trim()
      .replace(/[^\d]/g, '')
      .replace(/^1/, '');
    return updatedPhoneNumber;
  }

  const fetchContacts = useCallback(async () => {
    setIsLoading(true);
    try {
      const {
        canAskAgain,
        granted,
      }: { canAskAgain: boolean; granted: boolean } =
        await getPermissionsAsync();
      setCanAskAgain(canAskAgain);
      if (!granted) {
        setHasPermission(false);
        setIsLoading(false);
        return;
      }

      setHasPermission(true);
      const { data } = await getContactsAsync();

      const contactList: AllLocalRecipients[] =
        data
          ?.filter(
            (contact) =>
              (contact?.firstName || contact?.lastName) &&
              contact?.phoneNumbers?.some((phone) => {
                return getUnformattedPhoneNumber(phone.number ?? '').length ===
                  10
                  ? phone.number
                  : '';
              }),
          )
          ?.map((contact) => {
            const displayName = [contact?.firstName, contact?.lastName]
              .filter(Boolean)
              .join(' ');

            const emails = contact?.emails?.flatMap((email) =>
              email.email ? [email.email] : [],
            );

            const phoneNumbers = contact?.phoneNumbers?.map((phone) => {
              const cleanPhoneNumber = getUnformattedPhoneNumber(
                phone.number ?? '',
              );
              return cleanPhoneNumber.length === 10 ? cleanPhoneNumber : '';
            });

            return {
              id: contact?.id || getUuid(),
              displayName,
              ...(emails?.length && {
                email: { address: emails, verified: true },
              }),
              ...(phoneNumbers?.length && { phoneNumber: phoneNumbers }),
            };
          })
          ?.sort((a, b) => a.displayName.localeCompare(b.displayName)) || [];

      setContacts(contactList);
    } catch (err: unknown) {
      if (err instanceof Error) {
        setError(
          err.message ||
            'An unknown error occurred while fetching the contacts.',
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    contacts,
    hasPermission,
    canAskAgain,
    isLoading,
    error,
    fetchContacts,
  };
};
